import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { GrainOverlay } from "@/components/Bits";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Profile from "@/pages/Profile";
import Ministry from "@/pages/Ministry";
import Watch from "@/pages/Watch";
import Events from "@/pages/Events";
import Devotional from "@/pages/Devotional";
import Prayer from "@/pages/Prayer";
import Studio from "@/pages/Studio";
import Testimonies from "@/pages/Testimonies";
import Academy from "@/pages/Academy";
import CourseDetail from "@/pages/CourseDetail";
import Consulting from "@/pages/Consulting";
import Business from "@/pages/Business";
import Music from "@/pages/Music";
import Books from "@/pages/Books";
import Store from "@/pages/Store";
import Resources from "@/pages/Resources";
import Gallery from "@/pages/Gallery";
import TV from "@/pages/TV";
import TVShow from "@/pages/TVShow";
import TVWatch from "@/pages/TVWatch";
import TVLive from "@/pages/TVLive";
import HBTVAdvertise from "@/pages/HBTVAdvertise";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="min-h-screen flex items-center justify-center text-gold-400 font-serif text-2xl">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function Layout({ children }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <CartDrawer />
      <WhatsAppButton />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <GrainOverlay />
          <SmoothScroll />
          <ScrollToTop />
          <Toaster position="top-center" theme="dark" richColors />
          <Routes>
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/about" element={<Layout><About /></Layout>} />
            <Route path="/profile" element={<Layout><Profile /></Layout>} />
            <Route path="/ministry" element={<Layout><Ministry /></Layout>} />
            <Route path="/watch" element={<Layout><Watch /></Layout>} />
            <Route path="/events" element={<Layout><Events /></Layout>} />
            <Route path="/devotional" element={<Layout><Devotional /></Layout>} />
            <Route path="/prayer" element={<Layout><Prayer /></Layout>} />
            <Route path="/studio" element={<Layout><Studio /></Layout>} />
            <Route path="/stories" element={<Layout><Testimonies /></Layout>} />
            <Route path="/testimonies" element={<Navigate to="/stories" replace />} />
            <Route path="/academy" element={<Layout><Academy /></Layout>} />
            <Route path="/academy/:slug" element={<Layout><CourseDetail /></Layout>} />
            <Route path="/consulting" element={<Layout><Consulting /></Layout>} />
            <Route path="/business" element={<Layout><Business /></Layout>} />
            <Route path="/music" element={<Layout><Music /></Layout>} />
            <Route path="/books" element={<Layout><Books /></Layout>} />
            <Route path="/store" element={<Layout><Store /></Layout>} />
            <Route path="/resources" element={<Layout><Resources /></Layout>} />
            <Route path="/gallery" element={<Layout><Gallery /></Layout>} />
            <Route path="/tv" element={<Layout><TV /></Layout>} />
            <Route path="/tv/live" element={<Layout><TVLive /></Layout>} />
            <Route path="/tv/advertise" element={<Layout><HBTVAdvertise /></Layout>} />
            <Route path="/tv/show/:slug" element={<Layout><TVShow /></Layout>} />
            <Route path="/tv/watch/:episodeId" element={<Layout><TVWatch /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<Protected><Layout><Dashboard /></Layout></Protected>} />
            <Route path="/admin" element={<Protected adminOnly><Layout><Admin /></Layout></Protected>} />
            <Route path="/payment/success" element={<Layout><PaymentSuccess /></Layout>} />
            <Route path="/payment/cancel" element={<Layout><PaymentCancel /></Layout>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
